/**
 * Hide the bottom tab bar while a keyboard is open.
 *
 * On iOS, Keyboard resize "native" shrinks the WKWebView. A bar pinned with
 * position:fixed; bottom:0 then sits on the new layout bottom, just above
 * the keyboard. Hiding it lets the keyboard cover that spot. The web view
 * still shrinks, so a sheet can scroll the focused field into view.
 *
 * Android reports DidShow/DidHide. iOS reports Will* and Did*. Web uses
 * focus on a text field (phone width) or a visual viewport that has shrunk.
 */

const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'radio',
  'file',
  'submit',
  'reset',
  'range',
  'color',
  'hidden',
]);

const VIEWPORT_KEYBOARD_GAP = 120;
const PHONE_WIDTH = 768;

function isTextEntry(el) {
  if (!el || !el.tagName) return false;
  const tag = String(el.tagName).toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  if (tag !== 'input') return false;
  const type = String(el.type || 'text').toLowerCase();
  return !NON_TEXT_INPUTS.has(type);
}

function phoneLayout(win) {
  return (win.innerWidth || 0) <= PHONE_WIDTH;
}

function viewportShrunkByKeyboard(win) {
  const viewport = win.visualViewport;
  if (!viewport || !win.innerHeight) return false;
  return (win.innerHeight - viewport.height) > VIEWPORT_KEYBOARD_GAP;
}

function shouldTreatWebFocusAsKeyboard(el, win) {
  if (!isTextEntry(el)) return false;
  return phoneLayout(win) || viewportShrunkByKeyboard(win);
}

function setKeyboardOpen(open, doc) {
  const root = (doc || document).documentElement;
  root.classList.toggle('keyboard-open', Boolean(open));
}

function readKeyboardInset(win) {
  const vv = win && win.visualViewport;
  if (!vv || !win.innerHeight) return 0;
  const overlap = Math.round(win.innerHeight - vv.height - (Number(vv.offsetTop) || 0));
  return overlap > 1 ? overlap : 0;
}

function publishKeyboardInset(win, doc) {
  const root = (doc || document).documentElement;
  if (root.dataset && root.dataset.keyboardPreview === '1') return;
  const inset = readKeyboardInset(win || window);
  if (root.style && typeof root.style.setProperty === 'function') {
    root.style.setProperty('--keyboard-inset', `${inset}px`);
  }
}

function insideAssistant(el) {
  return Boolean(el && typeof el.closest === 'function' && el.closest('[data-assistant-thread]'));
}

function releaseDocumentScroll(win, doc) {
  if (win && typeof win.scrollTo === 'function') win.scrollTo(0, 0);
  const root = doc && (doc.scrollingElement || doc.documentElement);
  if (root) {
    root.scrollTop = 0;
    root.scrollLeft = 0;
  }
  if (doc && doc.body) {
    doc.body.scrollTop = 0;
    doc.body.scrollLeft = 0;
  }
}

/* After the keyboard closes, iOS can leave the visual viewport about 20px
   below the fixed header. Nudge that shift away. Do not jump a long page
   back to the top, and do not run this while the person is scrolling. */
function clearStuckViewportOffset(win) {
  const vv = win && win.visualViewport;
  const stuck = vv ? Number(vv.offsetTop) || 0 : 0;
  if (!win || typeof win.scrollTo !== 'function') return;
  const y = Number(win.pageYOffset || win.scrollY) || 0;
  // The login page stays about 17px down after the keyboard closes.
  if (y > 0 && y <= 40 && stuck <= 40) {
    win.scrollTo(0, 0);
    return;
  }
  if (stuck <= 0 || stuck > 80) return;
  win.scrollTo(0, y + 1);
  win.scrollTo(0, Math.max(0, y - stuck));
}

function onVendorInvitePage(doc) {
  const root = doc && doc.documentElement;
  return Boolean(root && root.classList && typeof root.classList.contains === 'function' && root.classList.contains('vendor-invite-page'));
}

function onAuthScreen(doc) {
  if (!doc || typeof doc.querySelector !== 'function') return false;
  return Boolean(doc.querySelector('.auth-screen'));
}

function settleAuthScroll(win, doc) {
  if (!onAuthScreen(doc)) return;
  releaseDocumentScroll(win, doc);
}

function scheduleAuthScrollReset(win, doc) {
  settleAuthScroll(win, doc);
  setTimeout(() => settleAuthScroll(win, doc), 80);
  setTimeout(() => settleAuthScroll(win, doc), 320);
}

function scrollFocusedIntoView(doc) {
  if (onVendorInvitePage(doc)) return;
  const el = doc && doc.activeElement;
  if (!el || el === doc.body || el === doc.documentElement) return;
  // The chat composer is already pinned. scrollIntoView walks every ancestor
  // and, on iOS, that walk lands the page back at the top.
  if (insideAssistant(el)) return;
  if (typeof el.scrollIntoView !== 'function') return;
  setTimeout(() => {
    try {
      el.scrollIntoView({ block: 'center', inline: 'nearest' });
    } catch (error) {
      el.scrollIntoView(true);
    }
  }, 50);
}

function installKeyboardTabBar({
  document: doc = document,
  window: win = window,
  Keyboard = null,
  native = false,
} = {}) {
  const root = doc.documentElement;

  const show = () => {
    root.classList.add('keyboard-open');
    publishKeyboardInset(win, doc);
    scrollFocusedIntoView(doc);
  };
  const hasClass = (name) => Boolean(root.classList && typeof root.classList.contains === 'function' && root.classList.contains(name));
  const hide = () => {
    const wasOpen = hasClass('keyboard-open');
    root.classList.remove('keyboard-open');
    if (!(root.dataset && root.dataset.keyboardPreview === '1') && root.style) {
      root.style.setProperty('--keyboard-inset', '0px');
    }
    if (onAuthScreen(doc)) {
      scheduleAuthScrollReset(win, doc);
      return;
    }
    if (wasOpen && !hasClass('vendor-invite-page')) clearStuckViewportOffset(win);
  };

  if (native && Keyboard && typeof Keyboard.addListener === 'function') {
    ['keyboardWillShow', 'keyboardDidShow'].forEach((name) => {
      Keyboard.addListener(name, show);
    });
    ['keyboardWillHide', 'keyboardDidHide'].forEach((name) => {
      Keyboard.addListener(name, hide);
    });
  }

  const onFocusIn = (event) => {
    if (native) {
      if (isTextEntry(event.target)) scrollFocusedIntoView(doc);
      return;
    }
    if (shouldTreatWebFocusAsKeyboard(event.target, win)) show();
  };

  const onFocusOut = () => {
    setTimeout(() => {
      if (isTextEntry(doc.activeElement)) return;
      if (onAuthScreen(doc)) {
        scheduleAuthScrollReset(win, doc);
        return;
      }
      if (native) {
        clearStuckViewportOffset(win);
        return;
      }
      if (!shouldTreatWebFocusAsKeyboard(doc.activeElement, win)) hide();
    }, 80);
  };

  const onViewport = () => {
    // visualViewport scroll fires on every document pan. Resetting scroll
    // there makes the page shake and can pin it in place.
    if (hasClass('vendor-invite-page')) return;
    if (hasClass('keyboard-open')) publishKeyboardInset(win, doc);
    if (native) return;
    if (shouldTreatWebFocusAsKeyboard(doc.activeElement, win)) show();
  };

  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('focusout', onFocusOut);
  // Do not synthesize clicks after a scroll. WKWebView already delivers the
  // button's click when the control is a real button with touch-action
  // manipulation. A touchend stand-in plus a duplicate-click guard was
  // dropping that click, so Show Completed never toggled.
  if (win.visualViewport && typeof win.visualViewport.addEventListener === 'function') {
    win.visualViewport.addEventListener('resize', onViewport);
  }

  return function uninstall() {
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('focusout', onFocusOut);
    if (win.visualViewport && typeof win.visualViewport.removeEventListener === 'function') {
      win.visualViewport.removeEventListener('resize', onViewport);
    }
    root.classList.remove('keyboard-open');
  };
}

export {
  VIEWPORT_KEYBOARD_GAP,
  isTextEntry,
  phoneLayout,
  viewportShrunkByKeyboard,
  shouldTreatWebFocusAsKeyboard,
  setKeyboardOpen,
  readKeyboardInset,
  publishKeyboardInset,
  insideAssistant,
  releaseDocumentScroll,
  clearStuckViewportOffset,
  onAuthScreen,
  settleAuthScroll,
  scrollFocusedIntoView,
  installKeyboardTabBar,
};
